import type { ContractorTimeEntry } from '../types';

export type ContractorDayBreakdownRow = {
  dateKey: string;
  label: string;
  minutes: number;
};

export type ContractorJobBreakdownRow = {
  projectId: string;
  minutes: number;
};

function workDateKeyLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatContractorBreakdownHours(minutes: number): string {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
}

export function formatContractorBreakdownDayLabel(workDate: Date): string {
  return workDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
}

/** Group submitted entries by timesheet, then sum minutes per calendar day. */
export function buildDayBreakdownByTimesheet(
  entries: ContractorTimeEntry[],
): Map<string, ContractorDayBreakdownRow[]> {
  const byTimesheet = new Map<string, Map<string, ContractorDayBreakdownRow>>();

  for (const entry of entries) {
    if (!entry.timesheetId || entry.minutes <= 0) continue;
    const dateKey = workDateKeyLocal(entry.workDate);
    const sheetDays = byTimesheet.get(entry.timesheetId) || new Map<string, ContractorDayBreakdownRow>();
    const existing = sheetDays.get(dateKey);
    if (existing) {
      existing.minutes += entry.minutes;
    } else {
      sheetDays.set(dateKey, {
        dateKey,
        label: formatContractorBreakdownDayLabel(entry.workDate),
        minutes: entry.minutes,
      });
    }
    byTimesheet.set(entry.timesheetId, sheetDays);
  }

  const result = new Map<string, ContractorDayBreakdownRow[]>();
  byTimesheet.forEach((dayMap, timesheetId) => {
    const rows: ContractorDayBreakdownRow[] = Array.from(dayMap.values()).sort((a, b) =>
      a.dateKey.localeCompare(b.dateKey),
    );
    result.set(timesheetId, rows);
  });
  return result;
}

/** Group submitted entries by timesheet, then sum minutes per job. */
export function buildJobBreakdownByTimesheet(
  entries: ContractorTimeEntry[],
): Map<string, ContractorJobBreakdownRow[]> {
  const byTimesheet = new Map<string, Map<string, number>>();

  for (const entry of entries) {
    if (!entry.timesheetId || !entry.projectId || entry.minutes <= 0) continue;
    const sheetJobs = byTimesheet.get(entry.timesheetId) || new Map<string, number>();
    sheetJobs.set(entry.projectId, (sheetJobs.get(entry.projectId) || 0) + entry.minutes);
    byTimesheet.set(entry.timesheetId, sheetJobs);
  }

  const result = new Map<string, ContractorJobBreakdownRow[]>();
  byTimesheet.forEach((jobMap, timesheetId) => {
    const rows = Array.from(jobMap.entries())
      .map(([projectId, minutes]) => ({ projectId, minutes }))
      .sort((a, b) => b.minutes - a.minutes || a.projectId.localeCompare(b.projectId));
    result.set(timesheetId, rows);
  });
  return result;
}

/** Prefer entry totals; fall back to the timesheet's stored project ids. */
export function resolveTimesheetJobs(
  timesheet: { id?: string; projectIds?: string[] },
  jobBreakdownByTimesheet: Map<string, ContractorJobBreakdownRow[]>,
): ContractorJobBreakdownRow[] {
  if (timesheet.id) {
    const fromEntries = jobBreakdownByTimesheet.get(timesheet.id);
    if (fromEntries && fromEntries.length > 0) return fromEntries;
  }
  return (timesheet.projectIds || [])
    .filter(Boolean)
    .map((projectId) => ({ projectId, minutes: 0 }));
}
